
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Send, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSupportTicket, useAddTicketMessage } from '@/hooks/useSupportTickets';
import { formatDate } from '@/utils/formatters';
import { Separator } from '@/components/ui/separator';
import StatusBadge from '@/components/common/StatusBadge';

const AuthorTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { data: ticket, isLoading } = useSupportTicket(id!);
  const addMessageMutation = useAddTicketMessage();
  const [replyMessage, setReplyMessage] = useState('');

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!replyMessage.trim() || !profile?.id || !id) return;
    
    try {
      await addMessageMutation.mutateAsync({
        ticketId: id,
        userId: profile.id,
        message: replyMessage.trim()
      });
      setReplyMessage('');
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'high':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  if (isLoading) {
    return (
      <MainLayout allowedRoles={['author']}>
        <div className="flex justify-center items-center h-full">
          <p>Loading ticket details...</p>
        </div>
      </MainLayout>
    );
  }

  if (!ticket) {
    return (
      <MainLayout allowedRoles={['author']}>
        <div className="flex flex-col items-center justify-center h-full">
          <h2 className="text-2xl font-bold mb-4">Ticket Not Found</h2>
          <Button onClick={() => navigate('/author/tickets')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to My Tickets
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout allowedRoles={['author']}>
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/author/tickets')}
          className="mr-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <PageHeader
          title={ticket.displayId || `Ticket #${id?.slice(0, 8)}`}
          description={ticket.subject}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket Messages */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Conversation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.messages?.map((message: any) => {
                const isAuthor = message.userId === ticket.authorId;
                const messageUser = isAuthor ? profile : { name: message.userName || 'Support Staff' };
                
                return (
                  <div 
                    key={message.id}
                    className={`flex ${isAuthor ? '' : 'justify-end'}`}
                  >
                    <div className={`flex gap-3 max-w-[80%] ${isAuthor ? '' : 'flex-row-reverse'}`}>
                      <Avatar className="h-10 w-10 mt-1">
                        <AvatarImage src={messageUser?.avatar_url} />
                        <AvatarFallback className={isAuthor ? 'bg-emerald-600' : 'bg-brand-500'}>
                          {messageUser?.name ? getInitials(messageUser.name) : <User className="h-5 w-5" />}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">
                            {messageUser?.name || 'Unknown'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(message.createdAt)}
                          </span>
                          {!isAuthor && (
                            <Badge variant="outline" className="text-xs">Staff</Badge>
                          )}
                        </div>
                        
                        <div className={`p-3 rounded-lg ${
                          isAuthor 
                            ? 'bg-gray-100 text-gray-800' 
                            : 'bg-brand-50 text-brand-800 border border-brand-200'
                        }`}>
                          {message.message}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
            
            {ticket.status !== 'closed' && (
              <CardFooter>
                <form onSubmit={handleSubmitReply} className="w-full">
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Type your message here..."
                      className="min-h-[100px]"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      disabled={addMessageMutation.isPending}
                    />
                    <div className="flex justify-end">
                      <Button 
                        type="submit" 
                        disabled={!replyMessage.trim() || addMessageMutation.isPending}
                        className="bg-brand-500 hover:bg-brand-600"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        {addMessageMutation.isPending ? 'Sending...' : 'Send Message'}
                      </Button>
                    </div>
                  </div>
                </form>
              </CardFooter>
            )}
          </Card>
        </div>
        
        {/* Ticket Details */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Ticket Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Status</h3>
                <StatusBadge status={ticket.status} />
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Priority</h3>
                <div>
                  <Badge className={`inline-flex items-center rounded-full px-3 py-1 capitalize ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority}
                  </Badge>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Category</h3>
                <p className="capitalize">{ticket.category}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Created Date</h3>
                <p>{formatDate(ticket.createdAt)}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h3>
                <p>{formatDate(ticket.updatedAt)}</p>
              </div>
              
              {ticket.status === 'closed' && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    This ticket has been closed. If you need further assistance, please create a new ticket.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default AuthorTicketDetail;
